import {animate, state, style, transition, trigger} from '@angular/animations';
import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSelect} from '@angular/material/select';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {AbstractContactModel, ContactType} from '@app/models/abstract-contact.model';
import {ContactModel} from '@app/models/contact.model';
import {GtProducts} from '@app/models/enums/gt-products.enum';
import {ServiceNumberModel} from '@app/models/service-number.model';
import {CallRegistry} from '@app/services/call-registry';
import {GtAttendantService} from '@app/services/gt-attendant.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtCommandService} from '@app/services/gt-command.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {GtLocalStorageService} from '@app/services/gt-localstorage.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {MainNumberCallsService} from '@app/services/main-number-calls.service';
import {Click2callComponent} from '@app/shared/click2call/click2call.component';
import {ContactDetailComponent} from '@app/shared/contact-detail/contact-detail.component';
import {CsvContactsImportComponent} from '@app/shared/csv-contacts-import/csv-contacts-import.component';
import {PhoneNumberFormatter, TimeAndDateFormatter, TimeAndDateFullFormatter} from '@app/shared/formatting.pipes';
import {SkillEditorComponent} from '@app/shared/skill-editor/skill-editor.component';
import {BuilderFactory} from '@app/shared/utils/builder.factory';
import {GtPhoneNumberUtil} from '@app/shared/utils/gt-phone-number.util';
import {GtServiceNumbersUtil} from '@app/shared/utils/gt-service-numbers.util';
import {YellowPagesSearchComponent} from '@app/shared/yellow-pages-search/yellow-pages-search.component';
import {ContactsColumnSettingsComponent} from '@app/swb/contacts-column-settings/contacts-column-settings.component';
import {ProfileComponent} from '@app/shared/profile/profile.component';
import {environment} from '@environment';
import {Contact, Department, Favourite} from '@gt-api';
import {TranslateService} from '@ngx-translate/core';
import {fromEvent, Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';

export enum ContactListType {
  SWB,
  USER
}

@Component({
  selector: 'gt-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('void', style({height: '0px', minHeight: '0', visibility: 'hidden'})),
      state('*', style({height: '*', visibility: 'visible'})),
      transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class ContactsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() oname: string;
  @Input() login: string;
  @Input() type: ContactListType = ContactListType.SWB;
  @Input() filterSelection: ContactType[] = [];
  dataSource = new MatTableDataSource<AbstractContactModel>();
  contactListTypes = ContactListType;
  products = GtProducts;
  isAdmin: boolean;

  groupTypes = [
    {text: 'contacts.empl', type: ContactType.EndUser},
    {text: 'contacts.answer_groups', type: ContactType.ServiceNumber},
    {text: 'contacts.external', type: ContactType.Contact},
    {text: 'contacts.favorites', type: ContactType.Favorite}
  ];

  displayedColumns: Array<string>;
  filterStr = '';
  selectedDepartmentId = 0;
  selectedEntry: AbstractContactModel | null;
  callRegistry: CallRegistry;
  favouriteStatusList: Favourite[] = [];

  @ViewChild('filter') filter: ElementRef;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('contactTypeSelector') select: MatSelect;
  pageIndex: number;
  pageSize = 10;
  sortId: string;
  sortDirection: string;

  departments: Department[];
  hasDepartments = environment.hasDepartments;
  hasShareContact = environment.hasShareContact;
  allowedVisibleNumberPatterns = environment.allowedVisibleNumberPatterns;

  private destroyed = new Subject<void>();
  private serviceNumbers: AbstractContactModel[] = [];
  private contacts: AbstractContactModel[] = [];

  constructor(
    protected authenticationService: GtAuthenticationService,
    private gtDirectoryService: GtDirectoryService,
    private mainNumberCallsService: MainNumberCallsService,
    private notificationService: GtNotificationService,
    private timeAndDateFormatter: TimeAndDateFormatter,
    private timeAndDateFullFormatter: TimeAndDateFullFormatter,
    private attendantService: GtAttendantService,
    private commandService: GtCommandService,
    private router: Router,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private localStorage: GtLocalStorageService,
    private domSanitizer: DomSanitizer
  ) {
    this.loadContactsSettings();
    this.dataSource.filterPredicate = (entry: AbstractContactModel) => {
      const filter: string = this.filterStr.replace(/ /g, '');
      const name = entry.name;
      const phoneNumber = entry.phoneNumber;
      const departmentId = this.selectedDepartmentId;
      const hasFavourite = this.filterSelection.some(groupType => groupType === ContactType.Favorite);
      return (
        (filter.length === 0 ||
          (name && name.toLocaleLowerCase().replace(/ /g, '').includes(filter.toLocaleLowerCase())) ||
          phoneNumber.replace(/ /g, '').includes(filter)) &&
        (this.filterSelection.length === 0 || this.showContactByType(this.filterSelection, entry) || (hasFavourite && entry.favourite)) &&
        // eslint-disable-next-line @typescript-eslint/dot-notation
        (!departmentId || !entry.isEndUser() || entry['departmentId'] === departmentId)
      );
    };

    this.authenticationService.isAdmin().subscribe(observer => {
      this.isAdmin = observer.valueOf();
    });
  }

  showContactByType(groups: ContactType[], entry: AbstractContactModel): boolean {
    let shouldShow = false;
    if (groups.includes(ContactType.Contact)) {
      // External filter will show internalContact and Contact
      shouldShow = entry.type === ContactType.InternalContact;
    }
    if (groups.includes(ContactType.EndUser)) {
      // Employee filter will show enduser and external endusers
      shouldShow = shouldShow || entry.type === ContactType.ExternalEndUser;
    }
    return groups.includes(entry.type) || shouldShow;
  }

  loadContactsSettings() {
    try {
      const settings = JSON.parse(this.localStorage.getItem('contacts-settings'));
      if (settings) {
        this.filterSelection = settings.filterSelection;
        this.pageIndex = settings.pageIndex;
        this.pageSize = settings.pageSize;
        this.sortId = settings.sortId;
        this.sortDirection = settings.sortDirection;
        this.selectedDepartmentId = settings.departmentId;
      }
    } catch (er) {
      console.log('could not load contacts-settings');
    }
  }

  saveContactsSettings() {
    const settings = {
      filterSelection: this.filterSelection,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortId: this.sortId,
      sortDirection: this.sortDirection,
      departmentId: this.selectedDepartmentId
    };

    this.localStorage.setItem('contacts-settings', JSON.stringify(settings));
  }

  ngOnInit() {
    this.oname = this.oname ? this.oname : this.authenticationService.oname;
    this.login = this.login ? this.login : this.authenticationService.login;

    this.initDisplayedColumns();
    this.gtDirectoryService.getOrganizationDepartments(this.authenticationService.oname).subscribe(r => {
      this.departments = r;
    });

    this.loadContacts();

    this.mainNumberCallsService.callRegistry.pipe(takeUntil(this.destroyed)).subscribe(calls => {
      this.callsUpdated(calls);
    });

    this.commandService.commands.pipe(takeUntil(this.destroyed)).subscribe(c => {
      switch (c.name) {
        case GtCommandService.COMMAND_SEARCH: {
          this.filter.nativeElement.focus();
          return false;
        }
      }
      return true;
    });
  }

  loadContacts() {
    this.gtDirectoryService
      .getContacts(this.oname, true)
      .pipe(takeUntil(this.destroyed))
      .subscribe(contacts => {
        this.contacts = contacts;
        this.setContacts();
      });
    this.gtDirectoryService
      .getServiceNumberModels(this.oname, true)
      .pipe(takeUntil(this.destroyed))
      .subscribe(serviceNumbers => {
        this.serviceNumbers = serviceNumbers;
        this.setContacts();
      });
  }

  getEndTime(entry: ContactModel) {
    if (entry.absenceEntry) {
      return this.timeAndDateFormatter.transform(entry.absenceEntry.endTime);
    }
    return '';
  }

  getMouseOverStatus(entry: ContactModel) {
    return entry.absenceEntry && (entry.imageId > 0 || entry.imageId)
      ? entry.absenceEntry.endTime
        ? `${entry.status} ${this.translateService.instant('global.until')}
    ${this.timeAndDateFullFormatter.transform(entry.absenceEntry.endTime)}`
        : `${entry.status}`
      : '';
  }

  private setContacts() {
    this.serviceNumbers = this.serviceNumbers.filter(snd => GtServiceNumbersUtil.visibleInDirectory(snd as ServiceNumberModel));
    // this.serviceNumbers.forEach(serviceNumberDataViewModel => {
    //   serviceNumberDataViewModel.queueData = ServiceNumberUtil.filterCallbackQueues(serviceNumberDataViewModel.queueData);
    // });
    this.dataSource.data = this.serviceNumbers.concat(this.contacts);
    this.dataSource.filter = new Date().toISOString();
  }

  ngAfterViewInit() {
    if (this.sort) {
      this.dataSource.sortingDataAccessor = (data: AbstractContactModel, property: string) => {
        switch (property) {
          case 'status':
            return data.status || '';
          case 'name':
            return data.name || '';
          case 'phoneNumber':
            return data.phoneNumber;
          case 'shortNumber':
            return data.shortNumber || '';
          case 'email':
            return data.email || '';
          case 'department':
            return data.department || '';
          case 'favourite':
            return data.favourite.toString();
          default:
            return '';
        }
      };
    }

    if (this.filter && this.select) {
      fromEvent(this.filter.nativeElement, 'keyup')
        .pipe(debounceTime(150), distinctUntilChanged())
        .pipe(takeUntil(this.destroyed))
        .subscribe(() => {
          this.paginator.pageIndex = 0;
          if (this.filter.nativeElement.value === '') {
            this.dataSource.filter = new Date().toISOString();
          } else {
            this.dataSource.filter = this.filter.nativeElement.value;
          }
        });
      this.select.selectionChange.subscribe(() => {
        this.paginator.pageIndex = 0;
        this.filterSelection = this.select.value;
        this.dataSource.filter = new Date().toISOString();
        this.saveContactsSettings();
      });
    }
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  callsUpdated(callRegistry: CallRegistry) {
    this.callRegistry = callRegistry;
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  clearFilter(): void {
    this.filterStr = '';
    this.dataSource.filter = new Date().toISOString();
  }

  backToTable() {
    this.selectedEntry = null;
  }

  transferCall(fullNumber: string, consult: boolean = false) {
    if (this.callRegistry && this.callRegistry.getCurrentCall()) {
      this.attendantService.transferCall(this.callRegistry.getCurrentCall().sessionId, fullNumber, consult).subscribe(response => {
        if (response.code === 200) {
          this.notificationService.normalNotification.next(this.translateService.instant('contacts.transfer_final'));
        }
      });
    }
  }

  sendSMS(contact: AbstractContactModel) {
    this.router.navigate(['/sms'], {queryParams: {receivers: contact.phoneNumber}});
  }

  isThisUserOrAdmin(contact: AbstractContactModel) {
    return this.isAdmin || this.authenticationService.login === contact.login;
  }

  showNumber(contact: AbstractContactModel) {
    if (contact.isServiceNumber()) {
      if (this.allowedVisibleNumberPatterns === undefined || this.allowedVisibleNumberPatterns.length === 0) {
        return true;
      }
      return this.allowedVisibleNumberPatterns.includes((contact as ServiceNumberModel).pattern);
    }
    return true;
  }

  showProfile(contact: ContactModel) {
    console.log('showProfile', contact);
    const dialogRef = this.dialog.open(ProfileComponent, {minWidth: 500, data: contact});
    dialogRef.componentInstance.canEdit = this.isThisUserOrAdmin(contact);
    dialogRef.componentInstance.back.subscribe(() => {
      dialogRef.close();
    });
  }

  shareContact(contact: AbstractContactModel) {
    const message = contact.name + '\n' + new PhoneNumberFormatter().transform(contact.phoneNumber);
    this.router.navigate(['/sms'], {queryParams: {msg: message}});
  }

  enableBlindTransfer(contact: AbstractContactModel): Observable<boolean> {
    if (contact.isEndUser() || contact.isServiceNumber()) {
      return of(true);
    }
    return this.authenticationService.hasOneOf(GtProducts.BCT_EXTERNAL_PRODUCT);
  }

  enableConsultedTransfer(contact: AbstractContactModel): Observable<boolean> {
    if (contact.isEndUser() || contact.isServiceNumber()) {
      return of(true);
    }
    return this.authenticationService.hasOneOf(GtProducts.CCT_EXTERNAL_PRODUCT);
  }

  showContact(contact: AbstractContactModel) {
    if (contact instanceof ContactModel) {
      const dialogRef = this.dialog.open(ContactDetailComponent, {minWidth: 500});
      dialogRef.componentInstance.contact = contact;
      dialogRef.componentInstance.back.subscribe(() => {
        dialogRef.close();
      });
    } else if (contact instanceof ServiceNumberModel && contact.getMainQueue()) {
      const dialogRef = this.dialog.open(SkillEditorComponent, {minWidth: 500, maxWidth: 1000, maxHeight: '100vh'});
      dialogRef.componentInstance.queueData = contact.getMainQueue();
      dialogRef.componentInstance.back.subscribe(() => {
        dialogRef.close();
      });
    }
  }

  createNewContact() {
    const newContact = BuilderFactory.createBuilder<Contact>()
      .id(-1)
      .ownerLogin(this.login)
      .ownerOrganization(this.oname)
      .global(true)
      .build();
    newContact['@type'] = 'Contact';
    const contactModel = ContactModel.convertContactModel(newContact);
    this.showContact(contactModel);
  }

  showWhitepages() {
    this.dialog.open(YellowPagesSearchComponent, {minWidth: 500});
  }

  showClick2Call(row?: ContactModel) {
    const dialogRef = this.dialog.open(Click2callComponent, {minWidth: 500});
    if (row) {
      dialogRef.componentInstance.canEdit = false;
      dialogRef.componentInstance.name = row.name;
      dialogRef.componentInstance.initialPhoneNumber = row.phoneNumber;
    }
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.attendantService.click2Call(result.phoneNumber, result.displayNumber).subscribe(response => {
          if (response.code === 200) {
            this.notificationService.normalNotification.next(
              this.translateService.instant('contacts.calling', {
                number: GtPhoneNumberUtil.toDisplay(result.phoneNumber),
                disp_number: GtPhoneNumberUtil.toDisplay(result.displayNumber)
              })
            );
          } else {
            this.notificationService.errorNotification.next(this.translateService.instant('Request failed'));
          }
        });
      }
    });
  }

  showImportDialog() {
    const dialogRef = this.dialog.open(CsvContactsImportComponent);
    dialogRef.componentInstance.closing.subscribe(() => {
      dialogRef.close();
    });
  }

  initDisplayedColumns() {
    if (this.type === ContactListType.USER) {
      this.displayedColumns = ['status', 'name', 'phoneNumber'];
    } else {
      const allAllowedColumns = ContactsColumnSettingsComponent.ALL_COLUMNS.slice();
      try {
        const storedColumns = localStorage.getItem('displayedColumns');
        if (storedColumns && storedColumns.length) {
          const columns: string[] = JSON.parse(storedColumns);
          this.displayedColumns = columns.map(c => (c === 'number' ? 'phoneNumber' : c)).filter(c => allAllowedColumns.find(a => a === c));
        }
      } catch (error) {
        console.error('Failed to load columns', error);
      }
      if (!this.displayedColumns || this.displayedColumns.length === 0) {
        this.displayedColumns = allAllowedColumns;
      }
    }
  }

  storeDisplayedColumns() {
    if (this.displayedColumns && this.displayedColumns.length) {
      localStorage.setItem('displayedColumns', JSON.stringify(this.displayedColumns));
    }
  }

  showColumnSettingsDialog() {
    const dialogRef = this.dialog.open(ContactsColumnSettingsComponent);
    dialogRef.componentInstance.displayedColumns = this.displayedColumns;
    dialogRef.componentInstance.displayedColumnsChanged.subscribe(displayedColumns => {
      this.displayedColumns = displayedColumns;
      this.storeDisplayedColumns();
    });
    dialogRef.componentInstance.closing.subscribe(() => {
      dialogRef.close();
    });
  }

  selectDepartment(departmentId: number) {
    this.selectedDepartmentId = departmentId;
    this.dataSource.filter = new Date().toISOString();
    this.saveContactsSettings();
  }

  addContactFavourite(contact: AbstractContactModel) {
    const favourite = this.newContactFavourite(contact);
    this.gtDirectoryService.addFavourite(this.oname, this.login, favourite).subscribe(
      () => {
        this.loadFavourites();
      },
      error => this.notificationService.errorNotification.next(`add contact favourite: ${JSON.stringify(error)}`)
    );
  }

  removeContactFavourite(contact: AbstractContactModel) {
    const favourite = this.newContactFavourite(contact);
    this.gtDirectoryService.removeFavourite(this.oname, this.login, favourite).subscribe(
      () => {
        this.loadFavourites();
      },
      error => this.notificationService.errorNotification.next(`remove contact favourite: ${JSON.stringify(error)}`)
    );
  }

  onSort(event: Sort) {
    this.sortId = event.active;
    this.sortDirection = event.direction;
    this.saveContactsSettings();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.saveContactsSettings();
  }

  getDomSanitizedUrl(contact: AbstractContactModel): SafeUrl {
    return this.domSanitizer.bypassSecurityTrustUrl('data:image/jpg;base64,' + contact.statusImage);
  }

  private newContactFavourite(contact: AbstractContactModel): Favourite {
    if (contact.isServiceNumber()) {
      return {'@type': 'ComponentFavourite', cid: 0, serviceNumber: contact.phoneNumber};
    } else {
      return {'@type': 'ContactFavourite', organization: this.oname, id: contact.id};
    }
  }

  private loadFavourites() {
    this.gtDirectoryService.getFavourites(this.oname, this.login).subscribe(response => {
      this.favouriteStatusList = response;
      const data = this.dataSource.data;
      data.forEach((entry: AbstractContactModel) => (entry.favourite = this.isFavourite(entry)));
      this.dataSource.data = data;
    });
  }

  private isFavourite(entry: AbstractContactModel) {
    /* eslint-disable @typescript-eslint/dot-notation */
    if (this.favouriteStatusList && entry.isServiceNumber()) {
      return this.favouriteStatusList.some(
        favourite =>
          favourite['@type'] === 'ComponentFavourite' && favourite['cid'] === 0 && favourite['serviceNumber'] === entry.phoneNumber
      );
    }
    if (this.favouriteStatusList) {
      return this.favouriteStatusList.some(favourite => favourite['@type'] === 'ContactFavourite' && favourite['id'] === entry.id);
    }
    return false;
    /* eslint-enable @typescript-eslint/dot-notation */
  }

  canShareContact(entry: AbstractContactModel) {
    const notServiceNumberOrServiceNumberWithAllowedPattern =
      !entry.isServiceNumber() || this.allowedVisibleNumberPatterns.find((p: string) => p === (entry as ServiceNumberModel).pattern);
    return this.hasShareContact && notServiceNumberOrServiceNumberWithAllowedPattern;
  }
}
